import { toSpaceKey } from '@toolproof-core/visualization/_pure/contract-helpers';
import type { SpaceKey, SpaceSlug } from '@toolproof-core/visualization/_contract/key';

import {
    DESCRIPTOR as BIOLOGY_DESCRIPTOR,
    SPACE_KEY as BIOLOGY_SPACE_KEY,
    SPACE_SLUG as BIOLOGY_SPACE_SLUG,
    type AdapterDescriptor as BiologyAdapterDescriptor,
} from '@/adapters/biology/_index';
import {
    DESCRIPTOR as CORE_DESCRIPTOR,
    SPACE_KEY as CORE_SPACE_KEY,
    SPACE_SLUG as CORE_SPACE_SLUG,
    type AdapterDescriptor as CoreAdapterDescriptor,
} from '@/adapters/core/_index';
import {
    createDescriptor as createEpisodeDescriptor,
    SPACE_KEY as EPISODE_SPACE_KEY,
    SPACE_SLUG as EPISODE_SPACE_SLUG,
    type AdapterDescriptor as EpisodeAdapterDescriptor,
} from '@/adapters/episode/_index';
import {
    DESCRIPTOR as PRIMITIVE_DESCRIPTOR,
    SPACE_KEY as PRIMITIVE_SPACE_KEY,
    SPACE_SLUG as PRIMITIVE_SPACE_SLUG,
    type AdapterDescriptor as PrimitiveAdapterDescriptor,
} from '@/adapters/primitive/_index';
import {
    DESCRIPTOR as WORKFLOW_DESCRIPTOR,
    SPACE_KEY as WORKFLOW_SPACE_KEY,
    SPACE_SLUG as WORKFLOW_SPACE_SLUG,
    type AdapterDescriptor as WorkflowAdapterDescriptor,
} from '@/workflow/_index';
import type { EpisodeAuditionOwner } from '@/_contract/episodeSupport';

/** Alias for the space the root route redirects to; distinct from Workflow's own identity. */
export const DEFAULT_SPACE_SLUG = WORKFLOW_SPACE_SLUG;
export const DEFAULT_SPACE_KEY = WORKFLOW_SPACE_KEY;

export type { BiologyAdapterDescriptor, WorkflowAdapterDescriptor, PrimitiveAdapterDescriptor, CoreAdapterDescriptor, EpisodeAdapterDescriptor };
export type RegisteredSpaceAdapterDescriptor = BiologyAdapterDescriptor | WorkflowAdapterDescriptor | PrimitiveAdapterDescriptor | CoreAdapterDescriptor | EpisodeAdapterDescriptor;

const SPACE_SLUG_BY_KEY: Readonly<Record<SpaceKey, SpaceSlug>> = {
    [BIOLOGY_SPACE_KEY]: BIOLOGY_SPACE_SLUG,
    [WORKFLOW_SPACE_KEY]: WORKFLOW_SPACE_SLUG,
    [PRIMITIVE_SPACE_KEY]: PRIMITIVE_SPACE_SLUG,
    [CORE_SPACE_KEY]: CORE_SPACE_SLUG,
    [EPISODE_SPACE_KEY]: EPISODE_SPACE_SLUG,
};

const SPACE_LABEL_BY_KEY: Readonly<Record<SpaceKey, string>> = {
    [BIOLOGY_SPACE_KEY]: 'Biology',
    [WORKFLOW_SPACE_KEY]: 'Workflow',
    [PRIMITIVE_SPACE_KEY]: 'Primitive',
    [CORE_SPACE_KEY]: 'Core',
    [EPISODE_SPACE_KEY]: 'Episodes',
};

/** Ring order of episode-space; a space appears only while its catalog has episodes. */
const EPISODE_RING_SPACES = [PRIMITIVE_DESCRIPTOR, WORKFLOW_DESCRIPTOR, CORE_DESCRIPTOR] as const;

const SPACE_DESCRIPTORS_BY_KEY = {
    [BIOLOGY_SPACE_KEY]: BIOLOGY_DESCRIPTOR,
    [WORKFLOW_SPACE_KEY]: WORKFLOW_DESCRIPTOR,
    [PRIMITIVE_SPACE_KEY]: PRIMITIVE_DESCRIPTOR,
    [CORE_SPACE_KEY]: CORE_DESCRIPTOR,
    [EPISODE_SPACE_KEY]: createEpisodeDescriptor(EPISODE_RING_SPACES.map((descriptor) => ({
        spaceSlug: SPACE_SLUG_BY_KEY[descriptor.baseDescriptor.spaceKey],
        label: SPACE_LABEL_BY_KEY[descriptor.baseDescriptor.spaceKey],
        episodes: descriptor.episodePresentation.episodes,
    })).filter((ring) => ring.episodes.length > 0)),
} as const satisfies Record<SpaceKey, RegisteredSpaceAdapterDescriptor>;

/** Routable spaces kept out of the Space select; episode-space is entered from XR. */
const SPACE_KEYS_HIDDEN_FROM_SELECT: ReadonlySet<SpaceKey> = new Set([EPISODE_SPACE_KEY]);

export type RegisteredSpaceNavigationOption = {
    spaceKey: SpaceKey;
    spaceSlug: SpaceSlug;
    label: string;
};

/** Every space a route may resolve to, including those hidden from the Space select. */
export function getRoutableSpaceNavigationOptions(): RegisteredSpaceNavigationOption[] {
    return Object.keys(SPACE_DESCRIPTORS_BY_KEY).map((spaceKey) => {
        const typedSpaceKey = spaceKey as SpaceKey;
        return {
            spaceKey: typedSpaceKey,
            spaceSlug: SPACE_SLUG_BY_KEY[typedSpaceKey],
            label: SPACE_LABEL_BY_KEY[typedSpaceKey],
        };
    });
}

export function getRegisteredSpaceNavigationOptions(): RegisteredSpaceNavigationOption[] {
    return getRoutableSpaceNavigationOptions().filter((option) => !SPACE_KEYS_HIDDEN_FROM_SELECT.has(option.spaceKey));
}

/** The Space select lists a hidden space only while it is active, so the select can show it. */
export function getSpaceSelectOptions(activeSpaceSlug: SpaceSlug): RegisteredSpaceNavigationOption[] {
    return getRoutableSpaceNavigationOptions().filter((option) =>
        option.spaceSlug === activeSpaceSlug || !SPACE_KEYS_HIDDEN_FROM_SELECT.has(option.spaceKey));
}

export function getSpaceAdapterDescriptor(spaceKey: SpaceKey): RegisteredSpaceAdapterDescriptor | undefined {
    return SPACE_DESCRIPTORS_BY_KEY[spaceKey];
}

export function getSpaceAdapterDescriptorBySlug(spaceSlug: SpaceSlug): RegisteredSpaceAdapterDescriptor | undefined {
    return getSpaceAdapterDescriptor(toSpaceKey(spaceSlug));
}

export function getRegisteredEpisodeAuditionOwners(): EpisodeAuditionOwner[] {
    return getRegisteredSpaceNavigationOptions().flatMap(({ spaceSlug }) => {
        const descriptor = getSpaceAdapterDescriptorBySlug(spaceSlug);
        const auditions = descriptor && 'episodePresentation' in descriptor
            ? descriptor.episodePresentation.support.auditions : undefined;
        return auditions ? [{ spaceSlug, auditions }] : [];
    });
}
